import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from "./ui/button";
import { GraduationCap, Menu, X, Calendar, User, ArrowLeft, Share2 } from "lucide-react";
import { blogPosts } from '../data/blog-mock';
import DOMPurify from 'dompurify';

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    // Find post by slug
    const foundPost = blogPosts.find(p => p.slug === slug);
    if (foundPost) {
      setPost(foundPost);
    } else {
      // Redirect to listing if not found
      navigate('/educacion');
    }
  }, [slug, navigate]);

  if (!post) return <div className="min-h-screen flex items-center justify-center">Cargando...</div>;

  return (
    <div className="min-h-screen bg-white font-sans text-brand-dark selection:bg-brand-blue selection:text-white">
      {/* Navigation */}
      <nav className="fixed w-full bg-white/95 backdrop-blur-md z-50 border-b border-gray-100">
        <div className="container mx-auto px-4 md:px-6 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img 
                src="https://customer-assets.emergentagent.com/job_e9aa37b0-2814-4b9e-8c4c-03a6b3b48503/artifacts/hffkvssr_LogoAppadrino_color.png" 
                alt="Appadrino Logo" 
                className="h-12 w-auto" 
            />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/educacion" className="flex items-center gap-2 text-gray-600 hover:text-brand-blue font-medium transition-colors">
                <ArrowLeft className="w-4 h-4" /> Volver al Blog
            </Link>
            <Button className="bg-brand-green hover:bg-brand-green/90 text-brand-dark font-bold rounded-full px-6 shadow-sm">
                Crear Cuenta
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button className="md:hidden p-2" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? <X className="w-6 h-6 text-brand-dark" /> : <Menu className="w-6 h-6 text-brand-dark" />}
          </button>
        </div>
      </nav>

      {/* Article Header */}
      <article className="pt-32 pb-20">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
            {/* Breadcrumb / Category */}
            <div className="flex items-center gap-3 text-sm mb-6">
                <Link to="/educacion" className="text-gray-500 hover:text-brand-blue font-medium">Blog</Link>
                <span className="text-gray-300">/</span>
                <span className="text-brand-blue font-bold uppercase tracking-wider text-xs bg-brand-lightBlue px-2 py-1 rounded-md">
                    {post.category}
                </span>
            </div>

            {/* Title */}
            <h1 className="text-3xl md:text-5xl font-extrabold text-brand-dark mb-6 leading-tight">
                {post.title}
            </h1>

            {/* Meta Data */}
            <div className="flex flex-wrap items-center gap-6 text-gray-500 text-sm mb-8 pb-8 border-b border-gray-100">
                <div className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    <span>Por <span className="font-bold text-brand-dark">{post.author}</span></span>
                </div>
                <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>{post.date}</span>
                </div>
                <Button variant="ghost" size="sm" className="ml-auto text-brand-blue gap-2 hover:bg-brand-lightBlue">
                    <Share2 className="w-4 h-4" /> Compartir
                </Button>
            </div>
        </div>

        {/* Featured Image */}
        <div className="container mx-auto px-4 md:px-6 max-w-5xl mb-12">
            <div className="rounded-3xl overflow-hidden shadow-2xl aspect-video md:aspect-[21/9]">
                <img 
                    src={post.image} 
                    alt={post.title} 
                    className="w-full h-full object-cover"
                />
            </div>
        </div>

        {/* Content Body */}
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
            <div 
                className="prose prose-lg prose-blue max-w-none text-gray-700 leading-loose
                prose-headings:font-extrabold prose-headings:text-brand-dark 
                prose-a:text-brand-blue prose-img:rounded-xl"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content) }} 
            />
            
            {/* Post-read CTA */}
            <div className="mt-16 p-8 bg-brand-lightBlue/50 rounded-3xl text-center">
                <h3 className="text-2xl font-bold text-brand-dark mb-4">¿Te gustó este artículo?</h3>
                <p className="text-gray-600 mb-6 max-w-md mx-auto">
                    Únete a Appadrino y comienza a aplicar estos consejos hoy mismo. Tu hijo te lo agradecerá mañana.
                </p>
                <Button className="bg-brand-blue hover:bg-brand-blue/90 text-white font-bold rounded-full px-8 py-6 text-lg shadow-xl shadow-brand-blue/20">
                    Comenzar a Ahorrar Gratis
                </Button>
            </div>
        </div>
      </article>

      {/* Simple Footer */}
      <footer className="bg-gray-50 py-12 border-t border-gray-200">
        <div className="container mx-auto px-4 md:px-6 text-center">
            <div className="flex items-center justify-center gap-2 mb-6">
                <GraduationCap className="w-6 h-6 text-brand-blue" />
                <span className="text-xl font-bold text-brand-blue">Appadrino</span>
            </div>
            <div className="text-sm text-gray-400">
                © 2025 Appadrino. Todos los derechos reservados.
            </div>
        </div>
      </footer>
    </div>
  );
};

export default BlogPost;
